import { useState, useEffect } from 'react';
import { Hash, Plus, Trash2, Loader2, Sparkles, RefreshCw } from 'lucide-react';
import DashboardLayout from '../components/DashboardLayout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { useAuth } from '../lib/auth';
import api from '../lib/api';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

export default function Keywords() {
  const { activeBusiness, user } = useAuth();
  const navigate = useNavigate();
  const plan = user?.plan || 'free';
  const kwLimit = (plan === 'pro' || plan === 'agency' || user?.role === 'admin') ? 50 : 5;
  const isPaid = plan === 'pro' || plan === 'agency' || user?.role === 'admin';
  const [keywords, setKeywords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newKeyword, setNewKeyword] = useState('');
  const [adding, setAdding] = useState(false);
  const [rescanning, setRescanning] = useState(false);

  const fetchKeywords = async () => {
    if (!activeBusiness) return;
    setLoading(true);
    try {
      const data = await api.getKeywords(activeBusiness._id);
      setKeywords(data);
    } catch {
      toast.error('Failed to load keywords');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchKeywords(); }, [activeBusiness]);

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!newKeyword.trim() || !activeBusiness) return;
    setAdding(true);
    try {
      await api.addKeyword(activeBusiness._id, newKeyword.trim());
      setNewKeyword('');
      fetchKeywords();
      toast.success('Keyword added!');
    } catch (err) {
      toast.error(err.message || 'Failed to add keyword');
    } finally {
      setAdding(false);
    }
  };

  const handleToggle = async (keywordId) => {
    try {
      const result = await api.toggleKeyword(keywordId);
      setKeywords(prev => prev.map(k => k._id === keywordId ? { ...k, enabled: result.enabled } : k));
    } catch {
      toast.error('Failed to toggle keyword');
    }
  };

  const handleDelete = async (keywordId) => {
    try {
      await api.deleteKeyword(keywordId);
      setKeywords(prev => prev.filter(k => k._id !== keywordId));
      toast.success('Keyword deleted');
    } catch {
      toast.error('Failed to delete keyword');
    }
  };

  const handleRescan = async () => {
    if (!activeBusiness?.website_url || !isPaid) return;
    setRescanning(true);
    try {
      const resp = await fetch('/api/scan', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ url: activeBusiness.website_url }) });
      if (!resp.ok) throw new Error('Rescan failed');
      const data = await resp.json();
      const newKws = (data.keywords || []).filter(kw => !keywords.some(k => k.keyword === kw));
      let added = 0;
      for (const kw of newKws) {
        if (keywords.length + added >= kwLimit) break;
        try { await api.addKeyword(activeBusiness._id, kw); added++; } catch {}
      }
      toast.success(`Added ${added} new keywords from rescan`);
      fetchKeywords();
    } catch (err) { toast.error(err.message || 'Rescan failed'); }
    finally { setRescanning(false); }
  };

  if (!activeBusiness) {
    return (
      <DashboardLayout title="Keywords">
        <div className="flex flex-col items-center justify-center py-20">
          <Hash className="w-12 h-12 text-[#9ca3af] mb-4" />
          <p className="text-sm text-[#5B667A]">Add a business first to manage keywords.</p>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Keywords">
      {/* Add keyword */}
      <Card className="p-4 rounded-xl border-[#E6E8EE] mb-6 shadow-[0_1px_2px_rgba(16,24,40,0.04)]">
        <form onSubmit={handleAdd} className="flex gap-3">
          <Input
            placeholder="Enter keyword..."
            value={newKeyword}
            onChange={(e) => setNewKeyword(e.target.value)}
            className="h-10 rounded-lg flex-1"
            data-testid="keywords-new-keyword-input"
          />
          <Button
            type="submit"
            disabled={adding || !newKeyword.trim()}
            className="h-10 rounded-lg font-medium text-sm"
            style={{ background: 'linear-gradient(180deg, #FF6A2A 0%, #FF4500 100%)' }}
            data-testid="keywords-add-keyword-button"
          >
            {adding ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Plus className="w-4 h-4 mr-1" /> Add Keyword</>}
          </Button>
        </form>
        <div className="flex items-center justify-between mt-3">
          <span className="text-xs text-[#5B667A]">{keywords.length}/{kwLimit} keywords used</span>
          <div className="flex items-center gap-3">
            {isPaid && keywords.length < kwLimit && (
              <button onClick={handleRescan} disabled={rescanning} className="text-xs text-[#2563EB] font-semibold hover:underline flex items-center gap-1">
                <RefreshCw className={`w-3 h-3 ${rescanning ? 'animate-spin' : ''}`} /> {rescanning ? 'Rescanning...' : 'Rescan website for more'}
              </button>
            )}
            {keywords.length >= kwLimit - 1 && plan === 'free' && (
              <button onClick={() => navigate('/settings')} className="text-xs text-[#FF4500] font-semibold hover:underline flex items-center gap-1">
                <Sparkles className="w-3 h-3" /> Upgrade for 50 keywords
              </button>
            )}
          </div>
        </div>
      </Card>

      {/* Keywords list */}
      {loading ? (
        <div className="space-y-2">
          {[1,2,3].map(i => <Skeleton key={i} className="h-14 w-full rounded-xl" />)}
        </div>
      ) : keywords.length === 0 ? (
        <Card className="p-12 rounded-xl border-[#E6E8EE] text-center">
          <Hash className="w-10 h-10 text-[#9ca3af] mx-auto mb-3" />
          <h3 className="text-base font-semibold mb-1" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>No keywords yet</h3>
          <p className="text-sm text-[#5B667A]">Keywords help us find relevant Reddit discussions for your product.</p>
        </Card>
      ) : (
        <div className="space-y-2">
          {keywords.map((kw) => (
            <Card key={kw._id} className="flex items-center justify-between px-4 py-3 rounded-xl border-[#E6E8EE]">
              <div className="flex items-center gap-3 flex-1">
                <span className={`text-sm font-medium ${kw.enabled ? 'text-[#0B0F17]' : 'text-[#9ca3af]'}`}>{kw.keyword}</span>
                {!kw.enabled && <Badge variant="secondary" className="text-xs">Disabled</Badge>}
              </div>
              <div className="flex items-center gap-3">
                <Switch
                  checked={kw.enabled}
                  onCheckedChange={() => handleToggle(kw._id)}
                  data-testid="keywords-toggle"
                />
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-[#9ca3af] hover:text-red-500"
                  onClick={() => handleDelete(kw._id)}
                  data-testid="keywords-delete"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}
    </DashboardLayout>
  );
}
